import React, { useState, useEffect } from 'react';
import { CancelIcon } from '../../assets/icons/icons';
import './style.css';

const DEFAULT_TARGETS = {
  Once: 1,
  Daily: 7,
  Weekly: 4,
  Monthly: 30,
  Forever: 1,
};

const getDefaultTargetForFrequency = (frequency) => DEFAULT_TARGETS[frequency] || 1;

const GoalModal = ({ isOpen, closeModal, addGoal, updateGoal, selectedGoal, markComplete }) => {
  const [goalName, setGoalName] = useState('');
  const [frequency, setFrequency] = useState('Once');
  const [description, setDescription] = useState('');
  const [targetCount, setTargetCount] = useState(String(getDefaultTargetForFrequency('Once')));
  const [targetCountTouched, setTargetCountTouched] = useState(false);

  // Reset fields when modal opens or selectedGoal changes
  useEffect(() => {
    if (isOpen && !selectedGoal) {
      // Clear fields when adding a new goal
      setGoalName('');
      setFrequency('Once');
      setDescription('');
      setTargetCount(String(getDefaultTargetForFrequency('Once')));
      setTargetCountTouched(false);
    } else if (selectedGoal) {
      // Populate the modal with the goal's existing data when editing
      setGoalName(selectedGoal.title);
      setFrequency(selectedGoal.frequency);
      setDescription(selectedGoal.description);
      const existingTarget = selectedGoal.targetcount ?? selectedGoal.targetCount ?? getDefaultTargetForFrequency(selectedGoal.frequency);
      setTargetCount(String(existingTarget));
      setTargetCountTouched(false);
    }
  }, [isOpen, selectedGoal]);

  const handleFrequencyChange = (value) => {
    setFrequency(value);
    if (!selectedGoal && !targetCountTouched) {
      setTargetCount(String(getDefaultTargetForFrequency(value)));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const parsedTarget = parseInt(targetCount, 10);
    const sanitizedTarget = !Number.isNaN(parsedTarget) && parsedTarget > 0
      ? parsedTarget
      : getDefaultTargetForFrequency(frequency);

    if (selectedGoal) {
      updateGoal({
        id: selectedGoal.id,
        title: goalName,
        frequency,
        description,
        targetcount: sanitizedTarget,
        status: selectedGoal.status === false ? true : selectedGoal.status,
      });
    } else {
      // Add a new goal
      addGoal({ title: goalName, frequency, description, targetcount: sanitizedTarget });
    }

    closeModal(); // Close the modal after submission
  };

  const handleMarkAsComplete = () => {
    if (selectedGoal) {
      // Mark the goal as complete by calling the markComplete function
      markComplete(selectedGoal.id); 
      closeModal(); // Close the modal after marking as complete
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={closeModal}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="goal-accordion-container">
          <h3>{selectedGoal ? 'Edit Goal' : 'Add to Goal Tracker'}</h3>
          <CancelIcon onClick={closeModal} className='cancel-icon'/>
        </div>
        <form onSubmit={handleSubmit}>
          <label>
            Name
            <input
              className='goal-tracker-input'
              type="text"
              placeholder="A task, habit, or goal..."
              value={goalName}
              onChange={(e) => setGoalName(e.target.value)}
              required
            />
          </label>
          <label>
            Frequency
            <select
              value={frequency}
              onChange={(e) => handleFrequencyChange(e.target.value)}
            >
              <option value="Once">Once</option>
              <option value="Daily">Daily</option>
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Forever">Forever</option>
            </select>
          </label>
          <label>
            Target Count
            <input
              type="number"
              min="1"
              step="1"
              value={targetCount}
              onChange={(e) => {
                setTargetCount(e.target.value);
                setTargetCountTouched(true);
              }}
            />
            <span className="goal-field-hint">How many times you want to complete this goal for the selected frequency.</span>
          </label>
          <label>
            Description
            <textarea
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className='extended-textarea'
            />
          </label>
          {selectedGoal ? (
            <div className='goal-btn-container'>
              <button type="submit">{selectedGoal.status === false ? 'Re-Activate Goal' : 'Update Goal'}</button>
              {selectedGoal.status !== false && (
                <button
                  className='goal-complete'
                  type="button"
                  onClick={handleMarkAsComplete} // Call the function to mark as complete
                >
                  Mark as complete
                </button>
              )}
            </div>
          ) : (
            <button type="submit">Add Goal</button>
          )}
        </form>
      </div>
    </div>
  );
};

export default GoalModal;
